import { apiService } from './apiService';

export interface ProfileUpdate {
  full_name?: string;
  phone?: string;
  avatar_url?: string;
  gender?: string;
  interests?: string[];
  sponsorship_preference?: string;
  notifications_enabled?: boolean;
  notification_preferences?: any;
  is_plus_user?: boolean;
  app_goal?: string | null;
  addiction_types?: string[];
  addiction_frequency?: string;
}

export interface Profile {
  id: string;
  email: string;
  full_name: string;
  phone?: string;
  avatar_url?: string;
  gender?: string;
  interests?: string[];
  sponsorship_preference?: string;
  clinic_id?: number;
  is_patient?: boolean;
  invitation_source?: string;
  invitation_accepted_at?: string;
  patient_status?: string;
  notifications_enabled?: boolean;
  notification_preferences?: any;
  is_plus_user?: boolean;
  created_at?: string;
  updated_at?: string;
  username?: string | null;
}

class ProfileService {
  async getProfile(): Promise<Profile | null> {
    try {
      console.log('📋 ProfileService: Fetching profile via API...');
      const response = await apiService.get<{ success: boolean; profile: Profile }>('/api/auth/profile/me');
      
      if (response.success && response.data?.profile) {
        console.log('✅ ProfileService: Profile fetched successfully');
        return response.data.profile;
      }
      
      console.warn('⚠️ ProfileService: API returned success=false or no profile:', response.error);
      return null;
    } catch (error) {
      console.error('❌ ProfileService: Exception fetching profile:', error);
      throw error;
    }
  }

  async updateProfile(updates: ProfileUpdate): Promise<{ success: boolean; profile?: Profile; error?: string }> {
    try {
      console.log('📝 ProfileService: Updating profile via API...', Object.keys(updates));
      const response = await apiService.put<{ success: boolean; profile: Profile }>('/api/auth/profile/me', updates);
      
      // Check both the API wrapper success and the server response success
      if (response.success && response.data?.success) {
        console.log('✅ ProfileService: Profile updated successfully');
        return { success: true, profile: response.data.profile };
      }
      
      // If wrapper succeeded but server returned error
      if (response.success && response.data && !response.data.success) {
        const serverError = (response.data as any).error || 'Server returned failure';
        console.error('❌ ProfileService: Server error updating profile:', serverError);
        return { success: false, error: serverError };
      }
      
      console.error('❌ ProfileService: Failed to update profile:', response.error);
      return { success: false, error: response.error || 'Failed to update profile' };
    } catch (error: any) {
      console.error('❌ ProfileService: Exception updating profile:', error);
      return { success: false, error: error.message || 'Failed to update profile' };
    }
  }

  async updateName(fullName: string): Promise<{ success: boolean; error?: string }> {
    return this.updateProfile({ full_name: fullName });
  }

  async updateUsername(username: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await apiService.put<{ success: boolean; username?: string; error?: string }>(
        '/api/auth/profile/me/username',
        { username }
      );
      if (response.success && response.data?.success) {
        return { success: true };
      }
      const serverError = response.data?.error || response.error || 'Failed to update username';
      return { success: false, error: serverError };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to update username' };
    }
  }

  async checkUsernameAvailability(username: string): Promise<{ available: boolean; valid: boolean; reason?: string; suggestion?: string }> {
    try {
      const encoded = encodeURIComponent(username);
      const response = await apiService.get<{ available: boolean; valid: boolean; reason?: string; suggestion?: string }>(
        `/api/username/check?username=${encoded}`
      );
      return response.data || { available: false, valid: false, reason: 'Request failed' };
    } catch {
      return { available: false, valid: false, reason: 'Request failed' };
    }
  }

  async updatePhone(phone: string): Promise<{ success: boolean; error?: string }> {
    return this.updateProfile({ phone });
  }

  async updateInterests(interests: string[]): Promise<{ success: boolean; error?: string }> {
    return this.updateProfile({ interests });
  }

  async updatePlusStatus(isPlusUser: boolean): Promise<{ success: boolean; error?: string }> {
    return this.updateProfile({ is_plus_user: isPlusUser });
  }

  async checkOnboardingStatus(): Promise<{ completed: boolean; profile?: Profile }> {
    try {
      const profile = await this.getProfile();
      
      if (!profile) {
        return { completed: false };
      }

      const hasInterests = profile.interests && profile.interests.length >= 3;
      const hasSponsorshipPreference = profile.sponsorship_preference !== null && profile.sponsorship_preference !== undefined;
      const hasGender = profile.gender !== null && profile.gender !== undefined;

      console.log('🔍 ProfileService: Onboarding status check:', {
        hasInterests,
        hasSponsorshipPreference,
        hasGender,
        completed: hasInterests && hasSponsorshipPreference && hasGender
      });

      return {
        completed: !!(hasInterests && hasSponsorshipPreference && hasGender),
        profile
      };
    } catch (error) {
      console.error('❌ ProfileService: Error checking onboarding status:', error);
      return { completed: false };
    }
  }

  // Like checkOnboardingStatus but PROPAGATES errors instead of coercing them
  // to `completed: false`. Used by the Google sign-in routing, where a transient
  // profile-fetch failure must NOT be mistaken for "new user" — otherwise a
  // returning user could be pushed back through onboarding. Callers should treat
  // a thrown error as "unknown — do not route to onboarding".
  //
  // NOTE: we call apiService.get directly (not getProfile) because getProfile
  // coerces a failed request into `null`, which would be indistinguishable from
  // a genuinely empty profile. Here a failed request MUST throw.
  async getOnboardingCompletionStrict(): Promise<boolean> {
    const response = await apiService.get<{ success: boolean; profile: Profile }>('/api/auth/profile/me');
    if (!response.success) {
      // A 404 means there is no profile row for this user. That is a genuinely
      // NEW user — e.g. a fresh Google sign-in (the bare profile row is created
      // by a DB trigger that can lag this check), or someone signing back in
      // right after deleting their account. They must be routed into the signup
      // / onboarding flow, so report "not complete" rather than throwing. We
      // only throw for transient failures (500 / network / 0) so a RETURNING
      // user is never pushed back through onboarding on a flaky request.
      if (response.status === 404) {
        return false;
      }
      throw new Error(response.error || 'Failed to fetch profile for onboarding status');
    }
    const profile = response.data?.profile;
    if (!profile) {
      // Request succeeded but there's no profile row yet → genuinely new user.
      return false;
    }
    const hasInterests = !!(profile.interests && profile.interests.length >= 3);
    const hasSponsorshipPreference = profile.sponsorship_preference !== null && profile.sponsorship_preference !== undefined;
    const hasGender = profile.gender !== null && profile.gender !== undefined;
    return hasInterests && hasSponsorshipPreference && hasGender;
  }
}

export const profileService = new ProfileService();
